import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

export type ImagePopupGroupItem = {
  id: string;
  imageUrl: string;
  alt?: string;
  linkUrl?: string;
  linkTarget?: "_blank" | "_self";
};

type ImagePopupGroupProps = {
  popups: ImagePopupGroupItem[];
  startOpen?: boolean;
};

type LegacyImagePopupProps = {
  id: string;
  image: string;
  alt?: string;
  link?: string;
  linkTarget?: "_blank" | "_self";
  startOpen?: boolean;
  onClose?: () => void;
  hAlign?: "left" | "center" | "right";
  vAlign?: "top" | "center" | "bottom";
  offsetX?: number;
  offsetY?: number;
};

type ImagePopupProps = ImagePopupGroupProps | LegacyImagePopupProps;

function kstTodayKey() {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return fmt.format(new Date());
}

function getHideTodayKey(id: string, todayKey: string) {
  return `popupHideToday:${id}:${todayKey}`;
}

function isHiddenToday(id: string, todayKey: string) {
  if (typeof window === "undefined") return false;

  try {
    return localStorage.getItem(getHideTodayKey(id, todayKey)) === "1";
  } catch {
    return false;
  }
}

function normalizePopupProps(props: ImagePopupProps): ImagePopupGroupProps {
  if ("popups" in props) {
    return {
      popups: props.popups,
      startOpen: props.startOpen,
    };
  }

  return {
    popups: [
      {
        id: props.id,
        imageUrl: props.image,
        alt: props.alt,
        linkUrl: props.link,
        linkTarget: props.linkTarget,
      },
    ],
    startOpen: props.startOpen,
  };
}

export default function ImagePopupGroup(props: ImagePopupProps) {
  const { popups, startOpen = true } = normalizePopupProps(props);
  const todayKey = useMemo(() => kstTodayKey(), []);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => new Set());
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= 768;
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const visiblePopups = useMemo(() => {
    if (!startOpen) return [];

    return popups.filter((popup) => {
      if (!popup.id || !popup.imageUrl) return false;
      if (dismissedIds.has(popup.id)) return false;
      return !isHiddenToday(popup.id, todayKey);
    });
  }, [dismissedIds, popups, startOpen, todayKey]);

  const closePopup = (id: string) => {
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const closeAll = useCallback(() => {
    setDismissedIds((prev) => {
      const next = new Set(prev);
      visiblePopups.forEach((popup) => next.add(popup.id));
      return next;
    });
  }, [visiblePopups]);

  const hideToday = (id: string) => {
    try {
      localStorage.setItem(getHideTodayKey(id, todayKey), "1");
    } catch {}

    closePopup(id);
  };

  useEffect(() => {
    if (visiblePopups.length === 0) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeAll();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeAll, visiblePopups.length]);

  if (visiblePopups.length === 0 || typeof document === "undefined") return null;

  return createPortal(
    <div
      aria-label="이미지 팝업"
      aria-modal="true"
      role="dialog"
      onClick={(event) => {
        if (event.target === event.currentTarget) closeAll();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,.58)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100dvh",
        padding: 12,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          flexWrap: isMobile ? "nowrap" : "wrap",
          gap: isMobile ? 12 : 16,
          alignItems: "flex-start",
          justifyContent: "center",
          maxWidth: "calc(100vw - 24px)",
          maxHeight: "calc(100dvh - 24px)",
          overflowX: "hidden",
          overflowY: "auto",
          padding: isMobile ? 0 : "4px 2px 8px",
          boxSizing: "border-box",
        }}
      >
        {visiblePopups.map((popup) => {
          const target = popup.linkTarget || "_blank";
          const image = (
            <img
              alt={popup.alt || popup.id}
              loading="lazy"
              src={popup.imageUrl}
              style={{ width: "100%", height: "auto", display: "block" }}
            />
          );

          return (
            <div
              key={popup.id}
              style={{
                width: isMobile ? "min(92vw, 420px)" : "min(28vw, 420px)",
                minWidth: isMobile ? undefined : 280,
                maxWidth: 420,
                background: "#0f172a",
                border: "1px solid rgba(255,255,255,.12)",
                borderRadius: 12,
                boxShadow: "0 10px 40px rgba(0,0,0,.4)",
                overflow: "hidden",
                flex: "0 0 auto",
              }}
            >
              {popup.linkUrl ? (
                <a
                  href={popup.linkUrl}
                  rel={target === "_blank" ? "noopener noreferrer" : undefined}
                  style={{ display: "block" }}
                  target={target}
                >
                  {image}
                </a>
              ) : (
                image
              )}

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  padding: 12,
                  background: "rgba(255,255,255,.04)",
                  borderTop: "1px solid rgba(255,255,255,.12)",
                }}
              >
                <button
                  onClick={() => hideToday(popup.id)}
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid #334155",
                    background: "#1f2937",
                    color: "#e5e7eb",
                    fontSize: 14,
                    cursor: "pointer",
                  }}
                  type="button"
                >
                  오늘 하루 보지 않기
                </button>
                <button
                  aria-label={`${popup.alt || popup.id} 닫기`}
                  onClick={() => closePopup(popup.id)}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 8,
                    border: "1px solid #334155",
                    background: "#0b1324",
                    color: "#e5e7eb",
                    fontSize: 14,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                  title="닫기"
                  type="button"
                >
                  X
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>,
    document.body
  );
}
